import { useState } from 'react';
import type { FormEvent } from 'react';
import { consultation } from '@/mocks/smart-farm';

const FORM_ADDR = 'https://readdy.ai/api/form/dajml5fh3l9b18n2bh20';

export default function Consultation() {
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    // 蜜罐检查：命中则静默返回成功反馈
    const honeypot = String(formData.get('website_alt') ?? '').trim();
    if (honeypot) {
      setStatus('success');
      form.reset();
      return;
    }
    formData.delete('website_alt');

    // 必填与格式校验
    const name = String(formData.get('name') ?? '').trim();
    const phone = String(formData.get('phone') ?? '').trim();
    if (!name) {
      setStatus('error');
      setErrorMsg('请填写您的姓名');
      return;
    }
    if (!phone) {
      setStatus('error');
      setErrorMsg('请填写您的联系方式');
      return;
    }
    if (!/^1[3-9]\d{9}$/.test(phone)) {
      setStatus('error');
      setErrorMsg('请输入正确的 11 位手机号码');
      return;
    }

    setSubmitting(true);
    setStatus('idle');
    setErrorMsg('');

    try {
      const res = await fetch(FORM_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (res.ok && parsed?.code === 'OK') {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
        const msg =
          (parsed?.meta as Record<string, unknown> | undefined)?.message ??
          parsed?.message ??
          (parsed?.meta as Record<string, unknown> | undefined)?.detail;
        setErrorMsg(typeof msg === 'string' && msg ? msg : '提交失败，请稍后重试');
      }
    } catch {
      setStatus('error');
      setErrorMsg('网络异常，请稍后重试');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="consultation" className="w-full px-4 md:px-6 py-16 md:py-24 bg-foreground-950 scroll-mt-20">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        {/* 左侧：文案 */}
        <div className="w-full">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-background-50/10 border border-background-50/20">
            <span className="w-2 h-2 rounded-full bg-accent-400"></span>
            <span className="text-xs md:text-sm text-background-100 tracking-wide whitespace-nowrap">
              方案咨询
            </span>
          </div>
          <h2 className="mt-5 font-heading font-bold text-background-50 text-3xl md:text-4xl leading-snug">
            {consultation.title}
          </h2>
          <p className="mt-5 text-sm md:text-base text-foreground-300 leading-relaxed max-w-xl">
            {consultation.desc}
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <a
              href="#consultation-form"
              className="inline-flex items-center justify-center gap-2 px-7 py-3 rounded-md bg-accent-500 text-foreground-950 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors hover:bg-accent-400"
            >
              {consultation.primaryLabel}
              <i className="ri-arrow-right-line"></i>
            </a>
            <a
              href="/demo"
              className="inline-flex items-center justify-center gap-2 px-7 py-3 rounded-md border border-background-50/30 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors hover:bg-background-50/10"
            >
              {consultation.secondaryLabel}
            </a>
          </div>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <div className="w-9 h-9 flex items-center justify-center rounded-full bg-accent-500/15 text-accent-300">
                <i className="ri-shield-check-line"></i>
              </div>
              <div className="mt-2 text-sm font-medium text-background-50">专业规划</div>
              <div className="mt-1 text-xs text-foreground-400 leading-relaxed">结合产业特点定制方案</div>
            </div>
            <div>
              <div className="w-9 h-9 flex items-center justify-center rounded-full bg-accent-500/15 text-accent-300">
                <i className="ri-service-line"></i>
              </div>
              <div className="mt-2 text-sm font-medium text-background-50">一体化交付</div>
              <div className="mt-1 text-xs text-foreground-400 leading-relaxed">咨询到运营全程服务</div>
            </div>
            <div>
              <div className="w-9 h-9 flex items-center justify-center rounded-full bg-accent-500/15 text-accent-300">
                <i className="ri-time-line"></i>
              </div>
              <div className="mt-2 text-sm font-medium text-background-50">快速响应</div>
              <div className="mt-1 text-xs text-foreground-400 leading-relaxed">提交后尽快与您联系</div>
            </div>
          </div>
        </div>

        {/* 右侧：表单 */}
        <div id="consultation-form" className="w-full">
          <div className="p-6 md:p-8 rounded-lg bg-background-50">
            <h3 className="font-heading font-bold text-foreground-950 text-xl md:text-2xl">
              {consultation.formTitle}
            </h3>
            <p className="mt-2 text-xs text-foreground-500">
              请留下您的需求信息，带 <span className="text-accent-600">*</span> 为必填项
            </p>

            <form
              data-readdy-form
              onSubmit={handleSubmit}
              className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4"
              noValidate
            >
              {/* 蜜罐字段（反垃圾） */}
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="form-hp-guard"
              />

              <div>
                <label htmlFor="sf-name" className="block text-sm text-foreground-700 mb-1.5">
                  姓名 <span className="text-accent-600">*</span>
                </label>
                <input
                  id="sf-name"
                  name="name"
                  type="text"
                  required
                  placeholder="请输入您的姓名"
                  className="w-full h-11 px-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 outline-none focus:border-accent-500 focus:ring-2 focus:ring-accent-200"
                />
              </div>

              <div>
                <label htmlFor="sf-phone" className="block text-sm text-foreground-700 mb-1.5">
                  联系方式 <span className="text-accent-600">*</span>
                </label>
                <input
                  id="sf-phone"
                  name="phone"
                  type="tel"
                  required
                  placeholder="请输入手机号码"
                  className="w-full h-11 px-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 outline-none focus:border-accent-500 focus:ring-2 focus:ring-accent-200"
                />
              </div>

              <div>
                <label htmlFor="sf-region" className="block text-sm text-foreground-700 mb-1.5">
                  所在地区
                </label>
                <input
                  id="sf-region"
                  name="region"
                  type="text"
                  placeholder="例如：浙江省杭州市"
                  className="w-full h-11 px-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 outline-none focus:border-accent-500 focus:ring-2 focus:ring-accent-200"
                />
              </div>

              <div>
                <label htmlFor="sf-company" className="block text-sm text-foreground-700 mb-1.5">
                  单位名称
                </label>
                <input
                  id="sf-company"
                  name="company"
                  type="text"
                  placeholder="请输入单位名称"
                  className="w-full h-11 px-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 outline-none focus:border-accent-500 focus:ring-2 focus:ring-accent-200"
                />
              </div>

              <div>
                <label htmlFor="sf-agriculture" className="block text-sm text-foreground-700 mb-1.5">
                  农业类型
                </label>
                <select
                  id="sf-agriculture"
                  name="agriculture_type"
                  defaultValue=""
                  className="w-full h-11 px-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 outline-none focus:border-accent-500 focus:ring-2 focus:ring-accent-200 cursor-pointer"
                >
                  <option value="" disabled>
                    请选择农业类型
                  </option>
                  <option value="标准农田">标准农田</option>
                  <option value="设施农业">设施农业</option>
                  <option value="果园与茶园">果园与茶园</option>
                  <option value="农业合作社">农业合作社</option>
                  <option value="农业产业园">农业产业园</option>
                  <option value="县域特色产业带">县域特色产业带</option>
                  <option value="其他">其他</option>
                </select>
              </div>

              <div>
                <label htmlFor="sf-timeline" className="block text-sm text-foreground-700 mb-1.5">
                  预计建设时间
                </label>
                <select
                  id="sf-timeline"
                  name="timeline"
                  defaultValue=""
                  className="w-full h-11 px-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 outline-none focus:border-accent-500 focus:ring-2 focus:ring-accent-200 cursor-pointer"
                >
                  <option value="" disabled>
                    请选择时间
                  </option>
                  <option value="1个月内">1 个月内</option>
                  <option value="1-3个月">1-3 个月</option>
                  <option value="3-6个月">3-6 个月</option>
                  <option value="6个月以上">6 个月以上</option>
                  <option value="暂不确定">暂不确定</option>
                </select>
              </div>

              <div className="sm:col-span-2">
                <label htmlFor="sf-requirement" className="block text-sm text-foreground-700 mb-1.5">
                  当前需求
                </label>
                <textarea
                  id="sf-requirement"
                  name="requirement"
                  rows={4}
                  maxLength={500}
                  placeholder="请简要描述您的农业经营现状与数字化需求（不超过 500 字）"
                  className="w-full px-3 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 outline-none focus:border-accent-500 focus:ring-2 focus:ring-accent-200 resize-none"
                ></textarea>
              </div>

              <div className="sm:col-span-2">
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full h-12 rounded-md bg-accent-500 text-foreground-950 text-sm font-semibold whitespace-nowrap cursor-pointer transition-colors hover:bg-accent-400 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? '提交中...' : '提交需求'}
                </button>
              </div>

              {status === 'success' && (
                <div className="sm:col-span-2 flex items-start gap-2 p-3 rounded-md bg-accent-100 border border-accent-200">
                  <i className="ri-checkbox-circle-line text-accent-600 mt-0.5"></i>
                  <p className="text-sm text-accent-900 leading-relaxed">{consultation.success}</p>
                </div>
              )}

              {status === 'error' && (
                <div className="sm:col-span-2 flex items-start gap-2 p-3 rounded-md bg-secondary-100 border border-secondary-200">
                  <i className="ri-error-warning-line text-secondary-700 mt-0.5"></i>
                  <p className="text-sm text-secondary-900 leading-relaxed">{errorMsg}</p>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}